/** An instance or user condition, kept local by the daemon's problem collector. */
export class InstanceConditionError extends Error {
  constructor(message, options) {
    super(message, options);
    this.name = 'InstanceConditionError';
  }
}

/** A condition of the host itself (measured load, database availability). Each occurrence stays local;
 *  the daemon's problem collector reports it once it repeats. */
export class HostConditionError extends InstanceConditionError {
  constructor(message, options) {
    super(message, options);
    this.name = 'HostConditionError';
  }
}

/** Database transport availability; reported once per sustained outage, never per loop tick. */
export class DatabaseUnavailableError extends HostConditionError {
  constructor(message, options) {
    super(message, options);
    this.name = 'DatabaseUnavailableError';
  }
}

export function isDatabaseUnavailable(error) { return classified(error, DatabaseUnavailableError); }

function classified(error, Type) {
  function match(value, seen) {
    if (!(value instanceof Error) || seen.has(value)) return false;
    if (value instanceof Type) return true;
    const path = new Set(seen).add(value);
    if (value instanceof AggregateError) return value.errors.length > 0 && value.errors.every(item => match(item, path));
    return match(value.cause, path);
  }
  return match(error, new Set());
}

/** Native cause chains and all-local aggregates preserve the one audience classification. */
export function isInstanceCondition(error) { return classified(error, InstanceConditionError); }

/** The same cause-chain rule for host conditions; a host condition is also an instance condition. */
export function isHostCondition(error) { return classified(error, HostConditionError); }

/** Expected desktop admission refusal, shared by producers and the host transport. */
export class DesktopAccessError extends Error {
  constructor(status, message, reason) {
    super(message);
    this.status = status;
    this.reason = reason;
    this.name = 'DesktopAccessError';
  }
}

/** Final-answer transport failure; adapters localize the notice while logs retain the diagnostic. */
export class FinalTextDeliveryError extends Error {
  name = 'FinalTextDeliveryError';
}

/** Preserve an Error's message; stringify every other thrown value without hiding conversion failures. */
export function errorText(error) {
  return error instanceof Error ? error.message : String(error);
}
